import { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, CheckCircle2, Undo2, Loader2, FileSpreadsheet, ArrowRight, Lock, Shuffle, Download, RotateCcw, AlertTriangle } from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

export default function ImportCatalog() {
  const { t, fmtCurrency } = useI18n();
  const { can } = useAuth();
  const canManage = can("products:write");
  const inputRef = useRef();
  const [file, setFile] = useState(null);
  const [batch, setBatch] = useState(null);
  const [busy, setBusy] = useState(false);

  const post = async (url, fd) => {
    setBusy(true);
    try { const r = await api.post(url, fd); setBatch(r.data); return r.data; }
    catch (e) { toast.error(apiErrorMessage(e, t)); }
    finally { setBusy(false); }
  };
  const dryRun = async () => { if (!file) return; const fd = new FormData(); fd.append("file", file); await post("/imports/supplier-offers?dry_run=true", fd); };
  const commit = async () => { const b = await post(`/imports/${batch.id}/commit`); if (b) toast.success(t("import.committed")); };
  const rollback = async () => { const b = await post(`/imports/${batch.id}/rollback`); if (b) toast.success(t("import.rolled_back")); };
  const reset = () => { setBatch(null); setFile(null); if (inputRef.current) inputRef.current.value = ""; };

  const downloadErrors = () => {
    const rows = [["row", "error"], ...(s.errors || []).map((e) => [e.row, e.error])];
    const csv = rows.map((r) => r.join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = "import-errors.csv"; a.click(); URL.revokeObjectURL(url);
  };

  const s = batch?.summary || {};
  const diff = s.diff || [];
  const changed = diff.filter((d) => d.delta !== "0.00" && !d.locked);

  return (
    <div className="space-y-6" data-testid="import-page">
      <div>
        <h1 className="font-heading text-2xl font-bold">{t("import.title")}</h1>
        <p className="text-sm text-slate-500">{t("import.subtitle")}</p>
      </div>

      <Card className="p-6">
        <div className="flex flex-wrap items-center gap-3">
          <input ref={inputRef} type="file" accept=".csv,.xlsx" className="hidden" data-testid="import-file"
            onChange={(e) => { setFile(e.target.files?.[0] || null); setBatch(null); }} />
          <Button type="button" variant="outline" onClick={() => inputRef.current?.click()} disabled={!canManage}>
            <FileSpreadsheet className="h-4 w-4" />{file ? file.name : t("import.choose_file")}
          </Button>
          <Button type="button" onClick={dryRun} disabled={!file || busy || !canManage} data-testid="import-dryrun">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}{t("import.validate")}
          </Button>
          {batch && <Button type="button" variant="ghost" onClick={reset} data-testid="import-reset"><RotateCcw className="h-4 w-4" />{t("import.reset")}</Button>}
        </div>
        <p className="mt-3 text-xs text-slate-400">{file ? t("import.columns_hint") : t("import.no_file")}</p>
      </Card>

      {batch && (
        <Card className="p-6" data-testid="import-summary">
          <div className="flex flex-wrap items-center gap-4">
            <span className="rounded bg-slate-100 px-3 py-1 text-sm dark:bg-slate-800">{t("import.status")}: <b>{t(`import.status_${batch.status}`)}</b></span>
            <span className="text-sm text-emerald-600" data-testid="import-ok-count">{t("import.ok")}: {s.ok ?? 0}</span>
            <span className="text-sm text-blue-600" data-testid="import-changed-count">{t("import.changed")}: {s.changed_count ?? 0}</span>
            <span className="text-sm text-amber-600" data-testid="import-warning-count">{t("import.warnings")}: {s.warning_count ?? 0}</span>
            <span className="text-sm text-red-600" data-testid="import-error-count">{t("import.errors")}: {s.error_count ?? 0}</span>
            <span className="text-sm text-slate-400">{t("import.total")}: {s.total ?? 0}</span>
          </div>

          {batch.status === "committed" && (
            <div className="mt-4 flex items-center gap-2 rounded-md border border-emerald-100 bg-emerald-50/60 p-3 text-sm text-emerald-800 dark:border-emerald-900/40 dark:bg-emerald-950/20 dark:text-emerald-300" data-testid="import-commit-summary">
              <CheckCircle2 className="h-4 w-4" />{t("import.commit_summary_desc", { ok: s.ok ?? 0, changed: s.changed_count ?? 0 })}
            </div>
          )}

          <Tabs defaultValue="changed" className="mt-5">
            <TabsList data-testid="import-tabs">
              <TabsTrigger value="all" data-testid="import-tab-all">{t("import.filter_all")} ({diff.length})</TabsTrigger>
              <TabsTrigger value="changed" data-testid="import-tab-changed">{t("import.filter_changed")} ({changed.length})</TabsTrigger>
              <TabsTrigger value="warnings" data-testid="import-tab-warnings">{t("import.filter_warnings")} ({s.warning_count ?? 0})</TabsTrigger>
              <TabsTrigger value="errors" data-testid="import-tab-errors">{t("import.filter_errors")} ({s.error_count ?? 0})</TabsTrigger>
            </TabsList>

            <TabsContent value="all"><DiffTable rows={diff} t={t} fmtCurrency={fmtCurrency} /></TabsContent>
            <TabsContent value="changed"><DiffTable rows={changed} t={t} fmtCurrency={fmtCurrency} /></TabsContent>

            <TabsContent value="warnings">
              {(s.warnings || []).length === 0 ? <Empty t={t} /> : (
                <div className="max-h-72 space-y-1 overflow-auto rounded border border-amber-100 bg-amber-50/40 p-3 text-xs dark:border-amber-900/40 dark:bg-amber-950/10">
                  {s.warnings.map((w, i) => (
                    <div key={i} className="flex items-center gap-2 text-amber-800 dark:text-amber-300" data-testid="import-warning-row">
                      <AlertTriangle className="h-3.5 w-3.5" />{t("import.row")} {w.row}: {t(`import.warning_${w.type}`)} {w.detail ? `— ${w.detail}` : ""}
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="errors">
              {(s.errors || []).length === 0 ? <Empty t={t} /> : (
                <>
                  <div className="mb-2 flex justify-end"><Button size="sm" variant="outline" onClick={downloadErrors} data-testid="import-download-errors"><Download className="h-4 w-4" />{t("import.download_errors")}</Button></div>
                  <div className="max-h-72 space-y-1 overflow-auto rounded border border-red-100 bg-red-50/50 p-3 text-xs dark:border-red-900/40 dark:bg-red-950/20">
                    {s.errors.map((e, i) => <div key={i} className="text-red-700 dark:text-red-300" data-testid="import-error-row">{t("import.row")} {e.row}: {e.error}</div>)}
                  </div>
                </>
              )}
            </TabsContent>
          </Tabs>

          {canManage && (
            <div className="mt-5 flex gap-2 border-t border-slate-100 pt-4 dark:border-slate-800">
              {batch.status === "dry_run" && (
                <Button type="button" onClick={commit} disabled={busy || (s.ok ?? 0) === 0} data-testid="import-commit">
                  <CheckCircle2 className="h-4 w-4" />{t("import.commit")}
                </Button>
              )}
              {batch.status === "committed" && (
                <Button type="button" variant="outline" onClick={rollback} disabled={busy} data-testid="import-rollback">
                  <Undo2 className="h-4 w-4" />{t("import.rollback")}
                </Button>
              )}
            </div>
          )}
        </Card>
      )}
    </div>
  );
}

const Empty = ({ t }) => <p className="py-6 text-center text-sm text-slate-400">{t("import.no_changes")}</p>;

function DiffTable({ rows, t, fmtCurrency }) {
  if (!rows || rows.length === 0) return <Empty t={t} />;
  return (
    <div className="max-h-96 overflow-auto rounded border border-slate-100 dark:border-slate-800" data-testid="import-diff-table">
      <table className="w-full text-sm">
        <thead className="sticky top-0 bg-slate-50 text-xs uppercase text-slate-500 dark:bg-slate-800/80">
          <tr>
            <th className="px-3 py-2 text-left">{t("import.product")}</th>
            <th className="px-3 py-2 text-right">{t("import.old_cost")}</th>
            <th className="px-3 py-2 text-center"></th>
            <th className="px-3 py-2 text-right">{t("import.new_cost")}</th>
            <th className="px-3 py-2 text-right">{t("import.change")}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((d) => {
            const delta = parseFloat(d.delta);
            const up = delta > 0, down = delta < 0;
            return (
              <tr key={d.product_id} className="border-t border-slate-100 dark:border-slate-800" data-testid="import-diff-row">
                <td className="px-3 py-2">
                  <div className="font-medium">{d.name}</div>
                  <div className="flex flex-wrap items-center gap-1 text-xs text-slate-400">
                    <span>{d.sku}</span>
                    {d.locked && <span className="inline-flex items-center gap-1 rounded bg-amber-100 px-1.5 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300"><Lock className="h-3 w-3" />{t("import.locked_protected")}</span>}
                    {d.flip && <span className="inline-flex items-center gap-1 rounded bg-violet-100 px-1.5 text-violet-700 dark:bg-violet-950/40 dark:text-violet-300"><Shuffle className="h-3 w-3" />{d.supplier_to}</span>}
                  </div>
                </td>
                <td className="px-3 py-2 text-right tabular-nums text-slate-500">{fmtCurrency(d.old_cost)}</td>
                <td className="px-3 py-2 text-center text-slate-300"><ArrowRight className="mx-auto h-3.5 w-3.5" /></td>
                <td className="px-3 py-2 text-right tabular-nums font-medium">{fmtCurrency(d.new_cost)}</td>
                <td className={`px-3 py-2 text-right tabular-nums font-medium ${up ? "text-red-600" : down ? "text-emerald-600" : "text-slate-400"}`}>
                  {delta === 0 ? "—" : `${up ? "+" : ""}${fmtCurrency(d.delta)} (${up ? "+" : ""}${d.delta_pct}%)`}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
